/**
 * Application Insights Service
 * 
 * Initializes and manages Azure Application Insights telemetry.
 * Provides integration with authentication telemetry utilities.
 * 
 * This is a recommendation from CODE_REVIEW_CONTEXT7.md (Section 10.2.2)
 * 
 * Setup:
 * 1. Install package: npm install @microsoft/applicationinsights-web --save
 * 2. Set environment variable: VITE_APPINSIGHTS_INSTRUMENTATION_KEY
 * 3. Application Insights will automatically track events and metrics in production
 */

// Type definition for Application Insights (avoids import error if package not installed)
// Once package is installed, this can be replaced with: import type { ApplicationInsights } from '@microsoft/applicationinsights-web';
type ApplicationInsights = {
  loadAppInsights: () => void;
  trackPageView: (pageView?: { name?: string; uri?: string; properties?: Record<string, string>; measurements?: Record<string, number> }) => void;
  trackEvent: (event: { name: string; properties?: Record<string, string>; measurements?: Record<string, number> }) => void;
  trackMetric: (metric: { name: string; average: number; sampleCount?: number; min?: number; max?: number }, properties?: Record<string, string>) => void;
  trackException: (exception: { exception: Error; severityLevel?: number; properties?: Record<string, string>; measurements?: Record<string, number> }) => void;
  trackTrace: (trace: { message: string; severityLevel?: number; properties?: Record<string, string> }) => void;
  setAuthenticatedUserContext: (userId: string, accountId?: string, storeInCookie?: boolean) => void;
  clearAuthenticatedUserContext: () => void;
};

let appInsights: ApplicationInsights | null = null;
let isInitialized = false;

/**
 * Initialize Application Insights
 * Should be called once at application startup (in main.tsx)
 * 
 * @param instrumentationKey - Application Insights instrumentation key (from Azure Portal)
 * @returns ApplicationInsights instance or null if initialization fails
 */
export function initializeApplicationInsights(
  instrumentationKey?: string
): ApplicationInsights | null {
  // Don't initialize twice
  if (isInitialized) {
    return appInsights;
  }

  // Only initialize in production
  if (import.meta.env.DEV) {
    console.log('[ApplicationInsights] Skipping initialization in development mode');
    isInitialized = true;
    return null;
  }

  // Get instrumentation key from environment or parameter
  const key = instrumentationKey || import.meta.env.VITE_APPINSIGHTS_INSTRUMENTATION_KEY;

  if (!key) {
    console.warn(
      '[ApplicationInsights] Instrumentation key not provided. ' +
      'Set VITE_APPINSIGHTS_INSTRUMENTATION_KEY environment variable or pass key parameter.'
    );
    isInitialized = true;
    return null;
  }

  try {
    // Dynamic import to avoid bundling Application Insights in development
    // This reduces bundle size when not needed
    // Use type assertion since package may not be installed yet
    import('@microsoft/applicationinsights-web').then((module: any) => {
      const { ApplicationInsights } = module;

      // Type assertion: ApplicationInsights from the module matches our type definition
      appInsights = new ApplicationInsights({
        config: {
          instrumentationKey: key,
          // Enable automatic page view tracking
          enableAutoRouteTracking: true,
          // Enable automatic exception tracking
          enableAutoExceptionTracking: true,
          // Enable automatic dependency tracking
          enableAutoDependencyTracking: true,
          // Enable automatic performance tracking
          enableAutoPerformanceTracking: true,
          // Disable automatic unhandled promise rejection tracking (we handle it manually)
          disableExceptionTracking: false,
          // Set sampling percentage (100% = track all events)
          samplingPercentage: 100,
          // Enable correlation headers
          enableCorsCorrelation: true,
          // Enable request headers
          enableRequestHeaderTracking: true,
          // Enable response headers
          enableResponseHeaderTracking: true,
        },
      }) as ApplicationInsights;

      // Initialize Application Insights
      appInsights.loadAppInsights();
      appInsights.trackPageView();

      console.log('[ApplicationInsights] Initialized successfully');
      isInitialized = true;
    }).catch((error) => {
      console.error('[ApplicationInsights] Failed to load Application Insights module:', error);
      isInitialized = true;
    });
  } catch (error) {
    console.error('[ApplicationInsights] Failed to initialize:', error);
    isInitialized = true;
    return null;
  }

  return appInsights;
}

/**
 * Get Application Insights instance
 * Returns null if not initialized or in development mode
 */
export function getApplicationInsights(): ApplicationInsights | null {
  return appInsights;
}

/**
 * Check if Application Insights is initialized and available
 */
export function isApplicationInsightsAvailable(): boolean {
  return appInsights !== null && isInitialized;
}

/**
 * Track custom event
 * 
 * @param name - Event name
 * @param properties - Event properties
 * @param measurements - Event measurements (numeric values)
 */
export function trackEvent(
  name: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (appInsights) {
    appInsights.trackEvent({ name, properties, measurements });
  }
}

/**
 * Track custom metric
 * 
 * @param name - Metric name
 * @param value - Metric value
 * @param count - Sample count (default: 1)
 * @param min - Minimum value (optional)
 * @param max - Maximum value (optional)
 * @param properties - Metric properties
 */
export function trackMetric(
  name: string,
  value: number,
  count?: number,
  min?: number,
  max?: number,
  properties?: Record<string, string>
): void {
  if (appInsights) {
    appInsights.trackMetric({ name, average: value, sampleCount: count || 1, min, max }, properties);
  }
}

/**
 * Track exception
 * 
 * @param exception - Error object
 * @param severityLevel - Severity level (0-4)
 * @param properties - Exception properties
 * @param measurements - Exception measurements
 */
export function trackException(
  exception: Error,
  severityLevel?: number,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (appInsights) {
    appInsights.trackException({
      exception,
      severityLevel,
      properties,
      measurements,
    });
  }
}

/**
 * Track page view
 * 
 * @param name - Page name
 * @param uri - Page URI
 * @param properties - Page view properties
 * @param measurements - Page view measurements
 */
export function trackPageView(
  name?: string,
  uri?: string,
  properties?: Record<string, string>,
  measurements?: Record<string, number>
): void {
  if (appInsights) {
    appInsights.trackPageView({ name, uri, properties, measurements });
  }
}

/**
 * Track trace (log message)
 * 
 * @param message - Trace message
 * @param severityLevel - Severity level (0-4)
 * @param properties - Trace properties
 */
export function trackTrace(
  message: string,
  severityLevel?: number,
  properties?: Record<string, string>
): void {
  if (appInsights) {
    appInsights.trackTrace({ message, severityLevel, properties });
  }
}

/**
 * Set authenticated user context
 * 
 * @param userId - User ID
 * @param accountId - Account ID (optional)
 */
export function setAuthenticatedUserContext(userId: string, accountId?: string): void {
  if (appInsights) {
    appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }
}

/**
 * Clear authenticated user context
 */
export function clearAuthenticatedUserContext(): void {
  if (appInsights) {
    appInsights.clearAuthenticatedUserContext();
  }
}
